import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import useInstructorView from '../components/hooks/useInstructorView'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Button, Card, CircularProgress, Container, Divider, Grid2 as Grid, Typography } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import { useAtomValue } from 'jotai'
import React, { useState } from 'react'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-coverflow'

import GroupSessionCard from '../components/cards/GroupSessionCard'
import UserCard from '../components/cards/UserCard'
import ImageGallery from '../components/common/ImageGallery'
import { TimeSlotsBookingCard } from '../components/forms/TimeSlotsBookingCard'
import SimpleHorizontalCards from '../components/layout/SimpleHorizontalCards'
import { availableSessionsByInstructorAtom, instructorAtom } from '../contexts/AtomPrivateSessionSearch'
import { InstructorViewAtom } from '../contexts/StatePageScope'

const PrivateLessonsSection = ({ sx = {} }) => {
  const [expanded, setExpanded] = useState(true)

  const availableSessionsByInstructor = useAtomValue(availableSessionsByInstructorAtom)

  if (availableSessionsByInstructor?.length === 0) {
    return null
  }

  return (
    <Box sx={{ ...sx }}>
      <Box sx={{ alignItems: 'center', mt: 1, mb: 1 }}>
        <Button size="Large" color="primary" aria-expanded={expanded} aria-label={expanded ? 'Collapse sessions' : 'Expand sessions'} onClick={() => setExpanded(!expanded)}>
          <Typography variant="h5">Book a Private Lesson with this Instructor</Typography>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Collapse in={expanded}>
        <Grid container justifyContent="center" alignItems="center" spacing={3}>
          <TimeSlotsBookingCard />
        </Grid>
      </Collapse>
    </Box>
  )
}

const GroupSessionsSection = ({ sessionChangeHook, sx = {} }) => {
  console.log('GroupSessionsSection is called')
  const groupSessions = useAtomValue(InstructorViewAtom.groupSessions)

  const [expanded, setExpanded] = useState(true)

  if (groupSessions.length === 0) {
    return null
  }

  return (
    <Box sx={{ ...sx }}>
      <Box sx={{ alignItems: 'center', mt: 1, mb: 1 }}>
        <Button size="Large" color="primary" aria-expanded={expanded} aria-label={expanded ? 'Collapse sessions' : 'Expand sessions'} onClick={() => setExpanded(!expanded)}>
          <Typography variant="h5"> Group Sessions by this Instructor</Typography>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Collapse in={expanded}>
        <SimpleHorizontalCards>
          {groupSessions.map((session) => {
            return <GroupSessionCard key={session.resourceId} session={session} mode="card" updateHook={sessionChangeHook} deleteHook={sessionChangeHook} />
          })}
        </SimpleHorizontalCards>
      </Collapse>
    </Box>
  )
}

const InstructorCard = () => {
  const instructor = useAtomValue(instructorAtom)
  return <UserCard user={instructor} clickable={false} collapsibleBio={true} shareButton={true} size="large" sx={{ mt: 0, border: 'none', '&:hover': {} }} />
}

/**
 * 教练详情页面 - 展示教练信息和他们的团体课程
 * 使用自定义hook分离数据逻辑和UI渲染
 */
export default function InstructorView() {
  const { instructor, loading, error, reload } = useInstructorView()

  // Combine pictures and videos into a unified media array
  const media = React.useMemo(() => {
    const pictureMedia = (instructor?.pictures || []).map((picture) => ({
      type: 'image',
      url: picture.url || picture.r2Url,
    }))

    const videoMedia = (instructor?.videos || []).map((video) => ({
      type: 'video',
      url: video.url || video.r2Url,
      coverUrl: video.coverImageUrl || video.coverUrl,
    }))

    return [...pictureMedia, ...videoMedia]
  }, [instructor?.pictures, instructor?.videos])

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 4 }}>
          <Typography color="error" align="center">
            {error}
          </Typography>
        </Box>
      </Container>
    )
  }

  if (!instructor) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 4 }}>
          <Typography align="center">Instructor not found</Typography>
        </Box>
      </Container>
    )
  }

  // Pure presentation component without business logic
  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Card>
        <InstructorCard />

        {/* Public Media Gallery with Swiper */}
        {media.length > 0 && (
          <ImageGallery
            media={media}
            title="Public Media Gallery"
            collapsible={true}
            defaultExpanded={true}
            autoplay={true}
            autoplayDelay={3000}
            effect="coverflow"
            sx={{ mt: 2 }}
          />
        )}

        {/* Instructor's private lessons */}
        <PrivateLessonsSection sx={{ mt: 2 }} />

        {/* Instructor's group sessions */}
        <GroupSessionsSection sessionChangeHook={reload} sx={{ mt: 2 }} />
      </Card>
    </Container>
  )
}
